import { Link, useSearch } from "@tanstack/react-location"
import moreJourneysLine from "assets/images/moreJourneysLine.svg"
import { useGraphqlQuery } from "graphql-mock"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { modifiers } from "ui-tools"
import { getCenteredSlice } from "../../tools/getCenteredSlice"
import { useJourneyNavigation } from "../../tools/useJourneyNavigation"
import { useCurrentUserContext } from "../AppShell/CurrentUserProvider"
import { JourneyThumbnail } from "../JourneyThumbnail"
import { CompletedCheck } from "./CompletedCheck"
import {
  findJourneyListItem,
  getFolderJourneys,
  getFolderStartJourney,
  getIsJourneyCompleted,
  getIsJourneyListItemCompleted,
  getJourneyListItemKey,
  getJourneyListItems,
  getBoxEdges,
  type BoxEdges,
  type YourJourneysListItem,
} from "./getJourneyListItems"
import { type Journey, query } from "./gql"
import {
  type ConnectorType,
  getConnectorType,
  JourneyConnector,
} from "./JourneyConnector"
import { JourneyFolderListItem } from "./JourneyFolderListItem"
import { YourJourneysListError } from "./YourJourneysListError"
import { YourJourneysListSkeleton } from "./YourJourneysListSkeleton"

import s from "./YourJourneysList.module.scss"

type YourJourneysListConnectedProps = {
  size: number
  onJourneyChange?: () => void
}
export const YourJourneysListConnected = ({
  size,
  onJourneyChange,
}: YourJourneysListConnectedProps) => {
  const { journeyIdParam, navigateToJourney } = useJourneyNavigation()
  const { data, error, isPending } = useGraphqlQuery(query, {
    page: 1,
    limit: 1000, // Big enough number to ensure we get all of 'em
  })

  const items = getJourneyListItems(
    data?.journeyFolders.nodes ?? [],
    data?.productCategories.nodes ?? []
  )

  return (
    <YourJourneysList
      isLoading={isPending}
      error={error}
      activeJourneyId={journeyIdParam}
      items={items}
      onJourneyChange={(journeyId: string) => {
        if (onJourneyChange) onJourneyChange()
        navigateToJourney(journeyId)
      }}
      size={size}
    />
  )
}

type YourJourneysListProps = {
  isLoading: boolean
  error: unknown
  activeJourneyId?: string
  items: YourJourneysListItem[]
  size: number
  onJourneyChange: (journeyId: string) => void
}
export const YourJourneysList = ({
  isLoading,
  error,
  activeJourneyId,
  items,
  size,
  onJourneyChange,
}: YourJourneysListProps) => {
  const {
    currentUser: { avatar },
  } = useCurrentUserContext()

  const activeItem = findJourneyListItem(items, activeJourneyId)
  const activeItemIndex = activeItem ? items.indexOf(activeItem) : -1
  const activeFolderId =
    activeItem?.type === "folder" ? activeItem.folder.id : undefined

  const [expandedFolderId, setExpandedFolderId] = useState(activeFolderId)
  const [previousActiveFolderId, setPreviousActiveFolderId] =
    useState(activeFolderId)

  // Navigating to a journey opens its folder and closes the rest. Adjusted
  // while rendering, so a folder doesn't flash closed when the list loads.
  if (activeFolderId !== previousActiveFolderId) {
    setPreviousActiveFolderId(activeFolderId)
    setExpandedFolderId(activeFolderId)
  }

  const toggleExpandedFolder = (folderId: string) => {
    setExpandedFolderId((currentFolderId) =>
      currentFolderId === folderId ? undefined : folderId
    )
  }

  const {
    items: centeredItems,
    itemsBefore,
    itemsAfter,
  } = getCenteredSlice(items, activeItemIndex, size)

  // The user's avatar marks the journey they're on, whatever its state
  const getJourneyItemThumbnail = (journey: Journey) => {
    const journeyImage = journey.teaserImageUrl ?? journey.backgroundImageUrl
    // Without an avatar, the journey keeps its own image
    const thumbnail =
      journey.id === activeJourneyId ? avatar || journeyImage : journeyImage

    return thumbnail || null
  }

  const renderJourney = (
    journey: Journey,
    connectorType: ConnectorType,
    completedBox: BoxEdges | null,
    isInFolder = false
  ) => (
    <JourneyListItem
      key={journey.id}
      journey={journey}
      connectorType={connectorType}
      completedBox={completedBox}
      isActive={journey.id === activeJourneyId}
      thumbnail={getJourneyItemThumbnail(journey)}
      isInFolder={isInFolder}
      onClick={() => onJourneyChange(journey.id)}
    />
  )

  const getIsExpandedFolder = (item: YourJourneysListItem) =>
    item.type === "folder" && item.folder.id === expandedFolderId

  // A completed item gets a green box, shared with the completed items next to
  // it. An open folder has a box of its own, so it doesn't get one.
  const completedBoxes = getBoxEdges(
    centeredItems,
    (item) => getIsJourneyListItemCompleted(item) && !getIsExpandedFolder(item)
  )

  const renderItem = (item: YourJourneysListItem, index: number) => {
    const completedBox = completedBoxes[index] ?? null
    const connectorType = getConnectorType({
      isFirst: index === 0,
      hasHiddenItemsBefore: itemsBefore.length > 0,
    })

    if (item.type === "journey")
      return renderJourney(item.journey, connectorType, completedBox)

    const { folder } = item

    // Selecting a folder opens its first journey not completed yet. The
    // folder holding the current journey has nothing new to open, so it
    // toggles instead.
    const selectFolder = () => {
      const startJourney = getFolderStartJourney(folder)

      if (folder.id === activeFolderId || !startJourney)
        toggleExpandedFolder(folder.id)
      else onJourneyChange(startJourney.id)
    }

    return (
      <JourneyFolderListItem
        key={getJourneyListItemKey(item)}
        folder={folder}
        connectorType={connectorType}
        completedBox={completedBox}
        isExpanded={getIsExpandedFolder(item)}
        activeJourneyId={activeJourneyId}
        onSelect={selectFolder}
      >
        {getFolderJourneys(folder).map((journey, journeyIndex) =>
          // The folder's own connector leads into its first journey
          renderJourney(
            journey,
            journeyIndex === 0 ? null : "default",
            null,
            true
          )
        )}
      </JourneyFolderListItem>
    )
  }

  const hasMoreItems = itemsBefore.length > 0 || itemsAfter.length > 0

  const getContent = () => {
    if (isLoading) return <YourJourneysListSkeleton />

    if (error) return <YourJourneysListError error={error} />

    return (
      <>
        {centeredItems.map(renderItem)}

        {hasMoreItems && (
          <ShowAllButton shouldShowLine={itemsAfter.length > 0} />
        )}
      </>
    )
  }

  return <div className={s.journeyList}>{getContent()}</div>
}

type JourneyListItemProps = {
  journey: Journey
  connectorType: ConnectorType
  completedBox: BoxEdges | null
  isActive: boolean
  thumbnail: string | null
  isInFolder: boolean
  onClick: () => void
}
const JourneyListItem = ({
  journey,
  connectorType,
  completedBox,
  isActive,
  thumbnail,
  isInFolder,
  onClick,
}: JourneyListItemProps) => {
  const { isLocked, title } = journey
  const isCompleted = getIsJourneyCompleted(journey)

  return (
    <div
      className={modifiers(s, "journeyItem", {
        hasCompletedBox: Boolean(completedBox),
        isBoxStart: Boolean(completedBox?.isStart),
        isBoxEnd: Boolean(completedBox?.isEnd),
      })}
    >
      {connectorType && <JourneyConnector type={connectorType} />}

      <div className={s.journeyItem__row}>
        <button
          type="button"
          className={modifiers(s, "journeyItem__button", {
            isActive,
            hasTrailingIcon: isCompleted,
          })}
          onClick={onClick}
          title={title ?? ""}
        >
          <div className={s.journeyItem__thumbnailColumn}>
            <JourneyThumbnail
              thumbnail={thumbnail}
              variant={isLocked ? "locked" : "default"}
              size={isInFolder ? "small" : "medium"}
            />
          </div>

          <div
            className={modifiers(s, "journeyItem__title", {
              isActive,
              isCertified: isCompleted,
              isLocked,
            })}
          >
            {title}
          </div>
        </button>

        {isCompleted && (
          <div
            className={modifiers(s, "journeyItem__trailingIcon", { isActive })}
          >
            <CompletedCheck />
          </div>
        )}
      </div>
    </div>
  )
}

const ShowAllButton = ({ shouldShowLine }: { shouldShowLine: boolean }) => {
  const { t } = useTranslation()
  const searchParams = useSearch()

  return (
    <div className={s.showAll__wrapper}>
      <img
        className={modifiers(s, "showAll__image", { shouldShowLine })}
        src={moreJourneysLine}
        alt=""
      />
      <Link to="list" search={searchParams} className={s.showAll__button}>
        {t("journey.SHOW_ALL")}
      </Link>
    </div>
  )
}
